import { reaction } from 'mobx'
import DynamoDB from 'aws-sdk/clients/dynamodb'
import { nanoid } from 'nanoid'

import { UserStore, analyze } from '~/stores'
import { TRequest, TFilter, TOffer, TNotification } from '~/types'
import { lowercaseObjectValues, objectToUpdateItemAttributes } from '~/utils'

export class ApiStore {
  // table names
  private tables = {
    users: 'users-preprod.part-scout.com',
    requests: 'requests-preprod.part-scout.com',
    offers: 'offers-preprod.part-scout.com',
    presets: 'saved-filters-preprod.part-scout.com',
    notifications: 'notifications-preprod.part-scout.com',
  }

  // dynamodb client instance
  public db: DynamoDB

  constructor(userStore: UserStore) {
    this.db = new DynamoDB({
      region: 'us-east-1',
      credentials: userStore.userCredentials,
    })

    // listen to credentials updates
    reaction(
      () => userStore.userCredentials,
      () => {
        this.db.config.credentials = userStore.userCredentials
      },
    )
  }

  // utils
  /** ApiStore.generateID is used to generate a unique identifier for put operations */
  private generateID = () => nanoid(12)

  // requests
  /** ApiStore.createRequest is used to create a new request */
  @analyze('requests', 'create-request')
  public createRequest(request: Partial<TRequest>) {
    const item = {
      ID: this.generateID(),
      Created: Date.now() / 1000,
      Updated: Date.now() / 1000,
      ...lowercaseObjectValues(request),
    }

    return this.db
      .putItem({
        TableName: this.tables.requests,
        Item: DynamoDB.Converter.marshall(item),
      })
      .promise()
      .then(() => item)
  }

  /** ApiStore.getRequests is used to list existing requests */
  @analyze('requests', 'get-requests')
  public getRequests(
    limit: number,
    attributes: Partial<DynamoDB.ScanInput> = {},
    startFrom?: Partial<TRequest>,
  ) {
    return this.db
      .scan({
        TableName: this.tables.requests,
        IndexName: 'Holder-Created',
        ExclusiveStartKey: startFrom && DynamoDB.Converter.marshall(startFrom),
        ConsistentRead: false,
        Limit: limit,
        ...attributes,
      })
      .promise()
      .then((res) =>
        res.Items.map((item) => DynamoDB.Converter.unmarshall(item) as TRequest),
      )
  }

  @analyze('requests', 'get-user-requests')
  public getRequestsByUserID(userID: string) {
    return this.db
      .query({
        TableName: this.tables.requests,
        IndexName: 'Holder-Updated',
        KeyConditionExpression: '#Holder = :Holder',
        ExpressionAttributeNames: {
          '#Holder': 'Holder',
        },
        ExpressionAttributeValues: {
          ':Holder': { S: userID },
        },
        Limit: 100,
      })
      .promise()
      .then((res) =>
        res.Items.map((item) => DynamoDB.Converter.unmarshall(item) as TRequest),
      )
  }

  /** ApiStore.getRequest is used to get request by ID */
  @analyze('requests', 'get-request')
  public getRequest(ID: string) {
    return this.db
      .getItem({
        TableName: this.tables.requests,
        Key: { ID: { S: ID } },
      })
      .promise()
      .then((res) => DynamoDB.Converter.unmarshall(res.Item) as TRequest)
  }

  /** ApiStore.countRequests is used to count existing requests */
  @analyze('requests', 'count-requests')
  public countRequests(attributes: Partial<DynamoDB.ScanInput> = {}) {
    return this.db
      .scan({
        TableName: this.tables.requests,
        IndexName: 'Holder-Created',
        Select: 'COUNT',
        ConsistentRead: false,
        ...attributes,
      })
      .promise()
      .then((c) => c.Count)
  }

  // presets
  /** ApiStore.createPreset is used to create a new preset */
  @analyze('presets', 'create-preset')
  public createPreset(filter: TFilter) {
    const item = {
      ID: this.generateID(),
      Created: Date.now() / 1000,
      Updated: Date.now() / 1000,
      ...filter,
    }

    return this.db
      .putItem({
        TableName: this.tables.presets,
        Item: DynamoDB.Converter.marshall(item),
      })
      .promise()
      .then(() => item)
  }

  /** ApiStore.getPresets is used to get existing presets */
  @analyze('presets', 'get-presets')
  public getPresets(userID) {
    return this.db
      .query({
        TableName: this.tables.presets,
        IndexName: 'Holder-Created',
        KeyConditionExpression: '#Holder = :Holder',
        ExpressionAttributeNames: {
          '#Holder': 'Holder',
        },
        ExpressionAttributeValues: {
          ':Holder': { S: userID },
        },
      })
      .promise()
      .then((res) =>
        res.Items.map((item) => DynamoDB.Converter.unmarshall(item) as TFilter),
      )
  }

  /** ApiStore.getPresets is used to delete existing preset */
  @analyze('presets', 'delete-preset')
  public deletePreset(presetID: string, userID: string) {
    return this.db
      .deleteItem({
        TableName: this.tables.presets,
        Key: DynamoDB.Converter.marshall({ ID: presetID, Holder: userID }),
      })
      .promise()
  }

  // offers
  @analyze('offers', 'create-offer')
  public createOffer(offer: Partial<TOffer>) {
    const item = {
      ID: this.generateID(),
      Created: Date.now() / 1000,
      Updated: Date.now() / 1000,
      ...offer,
    } as TOffer

    return this.db
      .putItem({
        TableName: this.tables.offers,
        Item: DynamoDB.Converter.marshall(item, { convertEmptyValues: true }),
      })
      .promise()
      .then(() =>
        this.db
          .updateItem({
            TableName: this.tables.requests,
            Key: {
              ID: { S: offer.RequestID },
            },
            UpdateExpression: 'ADD OffersCount :inc',
            ExpressionAttributeValues: {
              ':inc': { N: '1' },
            },
          })
          .promise(),
      )
      .then(() => item)
  }

  @analyze('offers', 'delete-offer')
  public deleteOffer(offerID: TOffer['ID'], requestID: TRequest['ID']) {
    return this.db
      .deleteItem({
        TableName: this.tables.offers,
        Key: {
          ID: { S: offerID },
          RequestID: { S: requestID },
        },
      })
      .promise()
  }

  @analyze('offers', 'get-offers')
  public getOffers(requestID: string) {
    return this.db
      .query({
        TableName: this.tables.offers,
        IndexName: 'RequestID-Created',
        KeyConditionExpression: '#RequestID = :RequestID',
        ExpressionAttributeNames: {
          '#RequestID': 'RequestID',
        },
        ExpressionAttributeValues: {
          ':RequestID': { S: requestID },
        },
      })
      .promise()
      .then((res) =>
        res.Items.map((item) => DynamoDB.Converter.unmarshall(item) as TOffer),
      )
  }

  @analyze('offers', 'get-user-offers')
  public getOffersByUserID(userID: string) {
    return this.db
      .query({
        TableName: this.tables.offers,
        IndexName: 'Holder-Created',
        KeyConditionExpression: '#Holder = :Holder',
        ExpressionAttributeNames: {
          '#Holder': 'Holder',
        },
        ExpressionAttributeValues: {
          ':Holder': { S: userID },
        },
        Limit: 100,
      })
      .promise()
      .then((res) =>
        res.Items.map((item) => DynamoDB.Converter.unmarshall(item) as TOffer),
      )
  }

  @analyze('offers', 'accept-offer')
  public acceptOffer(offerID: string, requestID: string) {
    return this.db
      .updateItem({
        TableName: this.tables.offers,
        Key: {
          ID: { S: offerID },
          RequestID: { S: requestID },
        },
        AttributeUpdates: {
          Status: {
            Value: { S: 'accepted' },
            Action: 'PUT',
          },
        },
      })
      .promise()
      .then(() =>
        this.db
          .updateItem({
            TableName: this.tables.requests,
            Key: {
              ID: { S: requestID },
            },
            UpdateExpression: 'ADD AcceptedOffersCount :inc',
            ExpressionAttributeValues: {
              ':inc': { N: '1' },
            },
          })
          .promise(),
      )
  }

  @analyze('offers', 'update-offer')
  public updateOffer(offerID: string, requestID: string, offer: Partial<TOffer>) {
    const item = {
      ...offer,
      ID: undefined,
      RequestID: undefined,
    }

    return this.db
      .updateItem({
        TableName: this.tables.offers,
        Key: {
          ID: { S: offerID },
          RequestID: { S: requestID },
        },
        ...objectToUpdateItemAttributes(item),
      })
      .promise()
  }

  // notifications
  @analyze('notifications', 'get-notifications')
  public getNotifications(userID: string) {
    return this.db
      .query({
        TableName: this.tables.notifications,
        IndexName: 'Holder-Created',
        KeyConditionExpression: '#Holder = :Holder',
        ExpressionAttributeNames: {
          '#Holder': 'Holder',
        },
        ExpressionAttributeValues: {
          ':Holder': { S: userID },
        },
        Limit: 100,
      })
      .promise()
      .then((res) =>
        res.Items.map((item) => DynamoDB.Converter.unmarshall(item) as TNotification),
      )
  }

  @analyze('notifications', 'view-notification')
  public setNotificationAsSeen(notificationID: string, userID: string) {
    return this.db
      .updateItem({
        TableName: this.tables.notifications,
        Key: {
          ID: { S: notificationID },
          Holder: { S: userID },
        },
        UpdateExpression: 'Set Seen = :seen',
        ExpressionAttributeValues: {
          ':seen': { BOOL: true },
        },
      })
      .promise()
  }

  @analyze('notifications', 'delete-notification')
  public deleteNotification(notificationID: string, userID: string) {
    return this.db
      .deleteItem({
        TableName: this.tables.notifications,
        Key: {
          ID: { S: notificationID },
          Holder: { S: userID },
        },
      })
      .promise()
  }
}
